import { keepPreviousData } from "@tanstack/react-query";
import { queryKeys } from "~/lib/query-keys";
import { getBrowserPlatformApiClient } from "~/lib/trpc/browser-client";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";

export type DirectIndexCatalog = Awaited<
  ReturnType<PlatformApiTrpcClient["directIndex"]["getCatalog"]["query"]>
>;
export type DirectIndexConstituentsResult = Awaited<
  ReturnType<PlatformApiTrpcClient["directIndex"]["getConstituents"]["query"]>
>;
export type DirectIndexConstituent =
  DirectIndexConstituentsResult["constituents"][number];
export type DirectIndexPreviewResult = Awaited<
  ReturnType<
    PlatformApiTrpcClient["directIndex"]["previewPerformance"]["query"]
  >
>;
export type DirectIndexStockSearchResult = Awaited<
  ReturnType<PlatformApiTrpcClient["assetSelector"]["search"]["query"]>
>;
export type DirectIndexResolvedAssetsResult = Awaited<
  ReturnType<PlatformApiTrpcClient["directIndex"]["resolveAssets"]["query"]>
>;
export type DirectIndexPreviewRange = DirectIndexPreviewResult["range"];
export type DirectIndexPreviewInput = {
  positions: ReadonlyArray<{ ticker: string; weightPct: string }>;
  rebalancing: "daily" | "weekly" | "monthly";
  range: DirectIndexPreviewRange;
};

export interface DirectIndexUniverseInput {
  kind: "index" | "sector";
  id: string;
}

// Structure changes at most daily (index syncs); keep it warm across the
// whole builder session so universe re-selection is instant.
const CATALOG_STALE_TIME_MS = 5 * 60_000;
const CONSTITUENTS_STALE_TIME_MS = 5 * 60_000;

export function getDirectIndexCatalogQueryOptions(input?: {
  platformApi?: PlatformApiTrpcClient;
}) {
  const platformApi = input?.platformApi ?? getBrowserPlatformApiClient();
  return {
    queryKey: queryKeys.directIndex.catalog,
    queryFn: async () => await platformApi.directIndex.getCatalog.query(),
    staleTime: CATALOG_STALE_TIME_MS,
  } as const;
}

export function getDirectIndexConstituentsQueryOptions(input: {
  universe: DirectIndexUniverseInput;
  platformApi?: PlatformApiTrpcClient;
}) {
  const platformApi = input.platformApi ?? getBrowserPlatformApiClient();
  return {
    queryKey: queryKeys.directIndex.constituents(
      input.universe.kind,
      input.universe.id,
    ),
    queryFn: async () =>
      await platformApi.directIndex.getConstituents.query({
        universe: input.universe,
      }),
    staleTime: CONSTITUENTS_STALE_TIME_MS,
  } as const;
}

export function getDirectIndexPreviewQueryOptions(input: {
  preview: DirectIndexPreviewInput;
  platformApi?: PlatformApiTrpcClient;
}) {
  const platformApi = input.platformApi ?? getBrowserPlatformApiClient();
  const { positions, rebalancing, range } = input.preview;
  const signature = positions
    .map((position) => `${position.ticker}:${position.weightPct}`)
    .sort()
    .join("|");
  return {
    queryKey: queryKeys.directIndex.preview(
      `${rebalancing}|${range}|${signature}`,
    ),
    queryFn: async () =>
      await platformApi.directIndex.previewPerformance.query({
        positions: [...positions],
        rebalancing,
        range,
      }),
    // The server caches per (positions, cadence, range, day); an hour of
    // client staleness just avoids refetch churn while range-toggling.
    staleTime: 60 * 60_000,
    placeholderData: keepPreviousData,
    enabled: positions.length > 0,
  } as const;
}

export function getDirectIndexStockSearchQueryOptions(input: {
  query: string;
  platformApi?: PlatformApiTrpcClient;
}) {
  const platformApi = input.platformApi ?? getBrowserPlatformApiClient();
  const normalizedQuery = input.query.trim().toLowerCase();
  return {
    queryKey: queryKeys.directIndex.stockSearch(normalizedQuery),
    queryFn: async () =>
      await platformApi.assetSelector.search.query({
        allowedSelectionKinds: ["onchain_asset"],
        assetClasses: ["equity"],
        availabilityFilter: "glider_supported",
        includeSparklines: false,
        lanes: ["canonical"],
        limit: 12,
        q: input.query.trim(),
      }),
    retry: false,
    staleTime: 30_000,
  } as const;
}

export function getDirectIndexStockSuggestionsQueryOptions(input: {
  query: string;
  platformApi?: PlatformApiTrpcClient;
}) {
  const platformApi = input.platformApi ?? getBrowserPlatformApiClient();
  const normalizedQuery = input.query.trim().toLowerCase();
  return {
    queryKey: queryKeys.directIndex.stockSuggestions(normalizedQuery),
    queryFn: async () =>
      await platformApi.assetSelector.suggestions.query({
        assetClasses: ["equity"],
        availabilityFilter: "glider_supported",
        includeSparklines: false,
        limit: 12,
        q: input.query.trim(),
      }),
    retry: false,
    staleTime: 60_000,
  } as const;
}

export async function resolveDirectIndexAssets(input: {
  assetCanonicalIds: readonly string[];
  platformApi?: PlatformApiTrpcClient;
}): Promise<DirectIndexResolvedAssetsResult> {
  const platformApi = input.platformApi ?? getBrowserPlatformApiClient();
  return await platformApi.directIndex.resolveAssets.query({
    assetCanonicalIds: [...input.assetCanonicalIds],
  });
}

export function getDirectIndexResolvedAssetsQueryOptions(input: {
  assetCanonicalIds: readonly string[];
  platformApi?: PlatformApiTrpcClient;
}) {
  const assetCanonicalIds = [...input.assetCanonicalIds].sort();
  return {
    queryKey: queryKeys.directIndex.resolvedAssets(assetCanonicalIds),
    queryFn: async () =>
      await resolveDirectIndexAssets({
        assetCanonicalIds,
        platformApi: input.platformApi,
      }),
    staleTime: CONSTITUENTS_STALE_TIME_MS,
  } as const;
}
